import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Loading, Modal, UnorderedList, ListItem } from '@carbon/react';

import { hideForm } from 'services/forms';
import { showToast } from 'services/toasts';
import { useDeleteElementMutation } from 'services/element';
import { informAboutRemovedElements } from '@/redux/new_services/element';

const cascadeFromConflict = (conflict) => {
  if (!conflict) return null;
  if (conflict.cascade_required) return conflict.cascade_required;
  if ((conflict.variant_groups || []).length > 0) return 'variant_group';
  if (conflict.pair_partner) return 'pair';
  return null;
};

const DeleteElementModal = ({ element, hideModal }) => {
  const dispatch = useDispatch();
  const [deleteElement, { isLoading }] = useDeleteElementMutation();
  const [conflict, setConflict] = useState(null);

  const doDelete = async (force, cascade = null) => {
    dispatch(hideForm());
    try {
      await deleteElement({ id: element.model.ID, force, cascade }).unwrap();
      const removedIds = [element.model.ID];
      const pairID = element?._pair_partner?.model?.ID || conflict?.pair_partner?.id;
      if (pairID) removedIds.push(pairID);
      dispatch(informAboutRemovedElements(removedIds));
      dispatch(showToast({
        kind: 'success',
        title: 'Element gelöscht',
        caption: force
          ? 'Das Element wurde gelöscht und aus den verwendeten Streams entfernt'
          : 'Das Element wurde erfolgreich gelöscht',
      }));
      hideModal();
    } catch (err) {
      if (err?.status === 409) {
        setConflict(err.data || {});
        return;
      }
      dispatch(showToast({
        kind: 'error',
        title: 'Fehler',
        caption: err?.data?.message || 'Das Element konnte nicht gelöscht werden',
      }));
      hideModal();
    }
  };

  const hasPair = Boolean(element?._pair_partner);

  if (conflict) {
    const streams = conflict.streams || [];
    const variantGroups = conflict.variant_groups || [];
    const cascadeRequired = cascadeFromConflict(conflict);

    return (
      <Modal
        open={true}
        danger={true}
        size="sm"
        primaryButtonText={
          cascadeRequired === 'variant_group'
            ? 'Gruppe und Medien löschen'
            : cascadeRequired === 'pair'
              ? 'Beide Formate löschen'
              : 'Trotzdem löschen'
        }
        secondaryButtonText="Abbrechen"
        modalHeading={conflict.message || 'Element wird verwendet'}
        onRequestClose={hideModal}
        onRequestSubmit={() => doDelete(true, cascadeRequired)}>
        {variantGroups.length > 0 && (
          <>
            <p style={{ marginBottom: '0.75rem' }}>
              Dieses Element ist Teil einer Varianten-Gruppe. Beim Löschen
              werden die gesamte Gruppe, alle Varianten und alle enthaltenen
              Medien soft-gelöscht:
            </p>
            <UnorderedList>
              {variantGroups.map((g) => (
                <ListItem key={g.id}>{g.name || `Varianten-Gruppe #${g.id}`}</ListItem>
              ))}
            </UnorderedList>
          </>
        )}
        {conflict.pair_partner && variantGroups.length === 0 && (
          <p style={{ marginBottom: '0.75rem' }}>
            Dieses Element ist Teil eines Hoch+Quer-Paars. Beim Löschen wird
            auch das Partnerformat "{conflict.pair_partner.name || `#${conflict.pair_partner.id}`}" soft-gelöscht.
          </p>
        )}
        {streams.length > 0 && (
          <>
            <p style={{ margin: '0.75rem 0' }}>
              Außerdem wird das Element aus diesen Streams entfernt:
            </p>
            <UnorderedList>
              {streams.map((s) => (
                <ListItem key={s.id}>{s.name || `Stream #${s.id}`}</ListItem>
              ))}
            </UnorderedList>
          </>
        )}
        <Loading active={isLoading} withOverlay={true} />
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      danger={true}
      size="sm"
      primaryButtonText="Löschen"
      secondaryButtonText="Abbrechen"
      modalHeading="Soll das Element wirklich gelöscht werden?"
      onRequestClose={hideModal}
      onRequestSubmit={() => doDelete(false)}>
      {hasPair && (
        <p>
          Dieses Element ist Teil eines Hoch+Quer-Paars. Im nächsten Schritt
          muss bestätigt werden, dass beide Formate gelöscht werden sollen.
        </p>
      )}
      <Loading active={isLoading} withOverlay={true} />
    </Modal>
  );
};

export default DeleteElementModal;
